import { ApolloError, gql, useApolloClient } from "@apollo/client";
import { useGenerateReportFileMutation, TrialFile, Trial_File_Type } from "@generated/graphql";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import { getTrialFileName } from "helpers/trials.helper";
import { useContext, useState } from "react";

const useGenerateTrialFile = (getExistingFiles?: () => TrialFile[] | null | undefined) => {
  const { addToast } = useContext(ToastContext);
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);
  const [generateFile] = useGenerateReportFileMutation();

  const onGenerateReportFileNull = () => {
    addToast({ type: ToastTypeEnum.WARNING, text: "Отчет формируется, повторите попытку позже" });
    setLoading(false);
  };

  const onGenerateTrialFile = async (trialId: string, reportType?: Trial_File_Type) => {
    setLoading(true);
    try {
      const res = await generateFile({
        variables: {
          input: {
            trialId,
            type: reportType || Trial_File_Type.RecruiterReport,
          },
        },
      });

      const trialFile = res.data?.generateReportFile;

      if (!trialFile?.file?.url) {
        onGenerateReportFileNull();
        return;
      }

      const existing = getExistingFiles?.() ?? [];

      client.cache.writeFragment({
        id: client.cache.identify({ __typename: "Trial", id: trialId }),
        fragment: gql`
          fragment AddTrialFile on Trial {
            recruiterReportFiles {
              id
              type
              file {
                id
                url
                __typename
              }
              __typename
            }
          }
        `,
        data: {
          recruiterReportFiles: [
            ...existing,
            {
              __typename: "TrialFile",
              id: trialFile.id,
              type: Trial_File_Type.RecruiterReport,
              file: {
                __typename: "File",
                id: trialFile.file.id,
                url: trialFile.file.url,
              },
            },
          ],
        },
      });

      fetch(trialFile.file.url)
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          const { trial } = trialFile;
          a.download = getTrialFileName({
            trial,
            examination: trial.examination,
            competencyMap: trial.examination.competencyMap,
            companyCandidate: trial.companyCandidate,
          });
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          addToast({ text: "Ошибка при скачивании файла", type: ToastTypeEnum.ERROR });
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (e) {
      setLoading(false);
      parseApolloError(e, { addToast, defaultText: "Ошибка при генерации отчета" });
      const error = e as ApolloError;
      console.error(error?.graphQLErrors?.[0]?.extensions?.originalError || error?.message || "Неизвестная ошибка");
    }
  };

  return { onGenerateTrialFile, loading };
};

export default useGenerateTrialFile;
